import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService, { FavoriteEntityType, UserFavorite } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const FAVORITES_QUERY_KEY = ['user-favorites'] as const;

export function useFavorites() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: FAVORITES_QUERY_KEY,
    queryFn: async () => {
      const result = await apiService.getFavorites();
      return result.favorites;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const favorites = data ?? [];

  function isFavorite(entityType: FavoriteEntityType, entityId: string): boolean {
    return favorites.some(
      (f) => f.entityType === entityType && f.entityId === entityId,
    );
  }

  function getFavoriteIdsByType(entityType: FavoriteEntityType): string[] {
    return favorites.filter((f) => f.entityType === entityType).map((f) => f.entityId);
  }

  const addMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: FavoriteEntityType; entityId: string }) =>
      apiService.addFavorite(entityType, entityId),
    onMutate: async ({ entityType, entityId }) => {
      await queryClient.cancelQueries({ queryKey: FAVORITES_QUERY_KEY });
      const previous = queryClient.getQueryData<UserFavorite[]>(FAVORITES_QUERY_KEY);
      queryClient.setQueryData<UserFavorite[]>(FAVORITES_QUERY_KEY, (old = []) => [
        ...old,
        {
          _id: `optimistic-${entityType}-${entityId}`,
          userId: '',
          entityType,
          entityId,
          createdAt: new Date().toISOString(),
        },
      ]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(FAVORITES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY });
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: FavoriteEntityType; entityId: string }) =>
      apiService.removeFavorite(entityType, entityId),
    onMutate: async ({ entityType, entityId }) => {
      await queryClient.cancelQueries({ queryKey: FAVORITES_QUERY_KEY });
      const previous = queryClient.getQueryData<UserFavorite[]>(FAVORITES_QUERY_KEY);
      queryClient.setQueryData<UserFavorite[]>(FAVORITES_QUERY_KEY, (old = []) =>
        old.filter((f) => !(f.entityType === entityType && f.entityId === entityId)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(FAVORITES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY });
    },
  });

  function toggleFavorite(entityType: FavoriteEntityType, entityId: string) {
    if (isFavorite(entityType, entityId)) {
      removeMutation.mutate({ entityType, entityId });
    } else {
      addMutation.mutate({ entityType, entityId });
    }
  }

  return {
    favorites,
    isLoading,
    isFavorite,
    getFavoriteIdsByType,
    toggleFavorite,
    isToggling: addMutation.isPending || removeMutation.isPending,
  };
}
